/**
 * Top-left phase countdown widget. Adapted from WhatIs.
 *
 * Big Oswald numerals + a label identifying the current phase. Shows the
 * remaining seconds counted down via a self-running RAF loop. Pulses warn
 * (yellow) under 5s, danger (red) under 3s.
 */

const STYLES = `
  @keyframes an-countdown-in {
    from { opacity: 0; transform: translateY(-6px); }
    to   { opacity: 1; transform: translateY(0);    }
  }
  @keyframes an-countdown-pulse {
    0%, 100% { transform: scale(1); }
    50%      { transform: scale(1.08); }
  }
  .an-countdown {
    position: absolute;
    top: 40px;
    left: 40px;
    z-index: 60;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    pointer-events: none;
    padding: 10px 22px 8px 22px;
    border-radius: 14px;
    background: linear-gradient(180deg, rgba(6,12,233,0.62) 0%, rgba(3,4,40,0.82) 100%);
    border: 2px solid var(--sg-primary, #FFC43D);
    box-shadow: 0 4px 30px rgba(0,0,0,0.4);
    animation: an-countdown-in 260ms ease-out;
    font-family: 'Oswald','Bebas Neue',Impact,sans-serif;
    color: #fff;
    min-width: 160px;
  }
  .an-countdown.hidden { display: none; }
  .an-countdown-value {
    font-size: 92px;
    font-weight: 700;
    line-height: 0.95;
    color: var(--sg-primary, #FFC43D);
    text-shadow: 0 0 18px rgba(255,196,61,0.55), 0 3px 6px rgba(0,0,0,0.6);
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.01em;
  }
  .an-countdown-label {
    font-size: 18px;
    font-weight: 700;
    letter-spacing: 0.28em;
    color: var(--sg-accent, #00BFA6);
    text-transform: uppercase;
    text-shadow: 0 2px 4px rgba(0,0,0,0.6);
  }
  .an-countdown.an-warn .an-countdown-value {
    animation: an-countdown-pulse 600ms ease-in-out infinite;
  }
  .an-countdown.an-danger { border-color: #F87171; }
  .an-countdown.an-danger .an-countdown-value {
    color: #F87171;
    text-shadow: 0 0 20px rgba(248,113,113,0.7), 0 3px 6px rgba(0,0,0,0.6);
    animation: an-countdown-pulse 420ms ease-in-out infinite;
  }
`;

function ensureStyles(): void {
  if (document.getElementById('an-countdown-style')) return;
  const s = document.createElement('style');
  s.id = 'an-countdown-style';
  s.textContent = STYLES;
  document.head.appendChild(s);
}

export interface CountdownHandle {
  el: HTMLDivElement;
  /**
   * Arm the countdown for a phase. `endsAtMs` is wall-clock ms.
   * `startsAtMs` (optional, also wall-clock): when provided AND in the future,
   * the widget freezes at the full phase duration (= endsAtMs - startsAtMs)
   * until that timestamp passes, then ticks down. Implements broadcast-delay-
   * aware countdown so viewers see the FULL round duration on screen.
   */
  setPhase: (label: string, endsAtMs: number, startsAtMs?: number) => void;
  hide: () => void;
  dispose: () => void;
}

export function createCountdown(mount: HTMLElement): CountdownHandle {
  ensureStyles();
  const el = document.createElement('div');
  el.className = 'an-countdown hidden';
  el.innerHTML = `
    <div class="an-countdown-value" data-role="value">0</div>
    <div class="an-countdown-label" data-role="label"></div>
  `;
  mount.appendChild(el);
  const valueEl = el.querySelector<HTMLDivElement>('[data-role="value"]')!;
  const labelEl = el.querySelector<HTMLDivElement>('[data-role="label"]')!;

  let endsAt = 0;
  let startsAt = 0;
  let currentLabel = '';
  let raf: number | null = null;

  function renderTick(): void {
    const now = Date.now();
    if (startsAt > 0 && now < startsAt) {
      const fullMs = Math.max(0, endsAt - startsAt);
      const str = String(Math.ceil(fullMs / 1000));
      if (valueEl.textContent !== str) valueEl.textContent = str;
      el.classList.remove('an-warn', 'an-danger');
      raf = requestAnimationFrame(renderTick);
      return;
    }
    const remainingMs = Math.max(0, endsAt - now);
    const seconds = Math.ceil(remainingMs / 1000);
    const str = String(seconds);
    if (valueEl.textContent !== str) valueEl.textContent = str;
    const remSec = remainingMs / 1000;
    el.classList.toggle('an-warn', remSec <= 5 && remSec > 3);
    el.classList.toggle('an-danger', remSec <= 3);
    if (remainingMs <= 0) {
      valueEl.textContent = '0';
      raf = null;
      return;
    }
    raf = requestAnimationFrame(renderTick);
  }

  function setPhase(label: string, endsAtMs: number, startsAtMs?: number): void {
    const normalizedLabel = label.toUpperCase();
    const nextStartsAt = startsAtMs && startsAtMs > Date.now() ? startsAtMs : 0;
    if (
      normalizedLabel === currentLabel &&
      Math.abs(endsAtMs - endsAt) < 250 &&
      Math.abs(nextStartsAt - startsAt) < 250
    ) {
      if (el.classList.contains('hidden')) el.classList.remove('hidden');
      if (raf === null) renderTick();
      return;
    }
    currentLabel = normalizedLabel;
    endsAt = endsAtMs;
    startsAt = nextStartsAt;
    labelEl.textContent = normalizedLabel;
    el.classList.remove('hidden');
    el.classList.remove('an-warn', 'an-danger');
    el.style.animation = 'none';
    void el.offsetWidth;
    el.style.animation = '';
    if (raf !== null) cancelAnimationFrame(raf);
    renderTick();
  }

  function hide(): void {
    el.classList.add('hidden');
    currentLabel = '';
    if (raf !== null) {
      cancelAnimationFrame(raf);
      raf = null;
    }
  }

  return { el, setPhase, hide, dispose() { hide(); el.remove(); } };
}
